/**
 * Toasts (spec 04-TELAS/0.3 · DS-52).
 *
 * O que faz este componente valer a pena: o botão **Desfazer**. A lei do produto é
 * que ato reversível oferece desfazer, e desfazer que só some da tela sem desfazer
 * nada é pior que não ter — então a ação recebe uma função de verdade, e o toast só
 * fecha depois de chamá-la.
 *
 * Fica na raiz da aplicação porque um toast não pertence a nenhuma tela: ele
 * sobrevive à navegação que o ato provocou.
 *
 * ## Duas correções do lote U1 (achado A11Y-05)
 *
 * **Uma região viva, permanente.** Uma região `aria-live` que nasce junto com o toast
 * não anuncia nada: o leitor de tela precisa estar observando a região ANTES de o texto
 * aparecer nela. Por isso a região é a PRÓPRIA pilha, que já existia desde a montagem —
 * ela só não estava declarada como viva.
 *
 * Uma região `sr-only` paralela, com o texto repetido, seria a solução óbvia e está
 * descartada de propósito: o texto passaria a existir DUAS vezes no documento, e tudo
 * que procura o toast pelo texto — os testes, e qualquer automação — passaria a achar
 * dois. Uma frase que aparece uma vez na tela tem de aparecer uma vez no DOM.
 *
 * O erro interrompe: `role="alert"` no cartão dele. O container é `status`/polite para o
 * resto, porque sucesso que corta a fala do leitor é grosseria, não urgência.
 *
 * **Seis segundos contam para quem lê, não para o relógio.** O toast some sozinho, e
 * quem usa teclado ou lupa levava o cursor até o "Desfazer" e o botão sumia debaixo dele.
 * Passar o ponteiro por cima ou levar o foco para dentro CONGELA o prazo; sair retoma de
 * onde parou. Sem isso, "ato reversível oferece desfazer" era uma promessa com prazo de
 * corrida de obstáculos.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import { Icone } from '../shell/icone.tsx'
import { useDestinoDosAvisos } from './sobreposicao.tsx'
import { useTextos } from '../textos.ts'
import { TEXTOS_DA_SOBREPOSICAO } from './sobreposicao-textos.ts'
import { elevacaoSobreAsAcoes, MARCA_DAS_ACOES_DO_DIALOGO, SELETOR_DAS_ACOES, type Caixa } from './toast-regras.ts'

export type TomDoToast = 'sucesso' | 'erro' | 'aviso' | 'info'

export interface AcaoDoToast {
  readonly rotulo: string
  readonly aoClicar: () => void | Promise<void>
}

export interface PedidoDeToast {
  readonly tom: TomDoToast
  readonly titulo: string
  /** Permite datas semânticas com detalhe acessível, sem duplicar o aviso no DOM. */
  readonly descricao?: ReactNode
  readonly acao?: AcaoDoToast | undefined
}

interface ToastNaTela extends PedidoDeToast {
  readonly id: number
}

const Contexto = createContext<(pedido: PedidoDeToast) => void>(() => {
  // Fora do provedor não há onde mostrar; silêncio é melhor que exceção numa
  // notificação — o ato principal já aconteceu.
})

export function useToast(): (pedido: PedidoDeToast) => void {
  return useContext(Contexto)
}

const VISUAL: Readonly<Record<TomDoToast, { icone: string; cor: string }>> = {
  sucesso: { icone: 'check-circle', cor: 'text-emerald-600' },
  erro: { icone: 'x-circle', cor: 'text-red-600' },
  aviso: { icone: 'triangle-alert', cor: 'text-amber-600' },
  info: { icone: 'info', cor: 'text-sky-600' },
}

/** 6 segundos é o do protótipo — tempo de ler e de clicar em Desfazer. */
const DURACAO_MS = 6000

interface Prazo {
  temporizador: ReturnType<typeof setTimeout>
  expiraEm: number
  restante: number
}

export function ProvedorDeToasts({ children }: { children: ReactNode }) {
  const destinoDosAvisos = useDestinoDosAvisos()
  /*
    O MESMO rótulo do diálogo, do mesmo catálogo: são o mesmo ato para quem lê, e duas
    traduções da palavra "fechar" divergiriam na primeira revisão feita num lugar só.
    Só alcança a preferência porque esta pilha desceu para dentro do shell (`raiz.tsx`).
  */
  const texto = useTextos(TEXTOS_DA_SOBREPOSICAO)
  const [toasts, setToasts] = useState<readonly ToastNaTela[]>([])
  const prazos = useRef(new Map<number, Prazo>())
  const proximoId = useRef(0)

  const fechar = useCallback((id: number) => {
    const prazo = prazos.current.get(id)
    if (prazo !== undefined) {
      clearTimeout(prazo.temporizador)
      prazos.current.delete(id)
    }
    setToasts((atuais) => atuais.filter((t) => t.id !== id))
  }, [])

  const agendar = useCallback(
    (id: number, ms: number) => {
      prazos.current.set(id, {
        temporizador: setTimeout(() => {
          fechar(id)
        }, ms),
        expiraEm: Date.now() + ms,
        restante: ms,
      })
    },
    [fechar],
  )

  /** Congela o prazo de todos: o ponteiro está em cima, ou o foco está dentro. */
  const pausar = useCallback(() => {
    for (const [id, prazo] of prazos.current) {
      clearTimeout(prazo.temporizador)
      prazos.current.set(id, { ...prazo, restante: Math.max(0, prazo.expiraEm - Date.now()) })
    }
  }, [])

  /** Retoma de onde parou — não reinicia, senão hover viraria toast eterno. */
  const retomar = useCallback(() => {
    for (const [id, prazo] of [...prazos.current]) agendar(id, prazo.restante)
  }, [agendar])

  const mostrar = useCallback(
    (pedido: PedidoDeToast) => {
      // O id vem de um contador monotônico: `Date.now()` daria colisão em dois toasts
      // do mesmo milissegundo, que é exatamente o que acontece quando um ato dispara
      // dois avisos.
      proximoId.current += 1
      const id = proximoId.current
      setToasts((atuais) => [...atuais, { ...pedido, id }])
      agendar(id, DURACAO_MS)
    },
    [agendar],
  )

  // Nenhum temporizador sobrevive à desmontagem do provedor.
  useEffect(() => {
    const abertos = prazos.current
    return () => {
      for (const prazo of abertos.values()) clearTimeout(prazo.temporizador)
      abertos.clear()
    }
  }, [])

  const valor = useMemo(() => mostrar, [mostrar])

  /*
    Os avisos não cobrem as ações do diálogo que pede (FX2 da v1.8.0, DS-654 (t); a regra mora em
    `toast-regras.ts`). Só com toast na tela, dentro de um diálogo, e só se o rodapé dele tem a marca:
    sem ela, nenhuma escuta nasce e a pilha fica onde sempre esteve. A medida refaz quando o painel
    rola (o rodapé que não é fixo anda com o conteúdo), quando a janela muda e quando a pilha cresce.
  */
  const pilha = useRef<HTMLDivElement | null>(null)
  const elevacaoAplicada = useRef(0)
  const [elevacao, setElevacao] = useState(0)
  useLayoutEffect(() => {
    const aplicar = (nova: number): void => {
      if (nova === elevacaoAplicada.current) return
      elevacaoAplicada.current = nova
      setElevacao(nova)
    }
    const elemento = pilha.current
    const rodape = destinoDosAvisos?.querySelector<HTMLElement>(`[${MARCA_DAS_ACOES_DO_DIALOGO}]`) ?? null
    if (elemento === null || destinoDosAvisos === null || rodape === null || toasts.length === 0) {
      aplicar(0)
      return
    }
    const caixaDe = (e: Element): Caixa => {
      const r = e.getBoundingClientRect()
      return { top: r.top, bottom: r.bottom, left: r.left, right: r.right }
    }
    const medir = (): void => {
      const painel = caixaDe(rodape.parentElement ?? rodape)
      const visivel: Caixa = {
        top: Math.max(0, painel.top),
        bottom: Math.min(window.innerHeight, painel.bottom),
        left: Math.max(0, painel.left),
        right: Math.min(window.innerWidth, painel.right),
      }
      const acoes = Array.from(rodape.querySelectorAll(SELETOR_DAS_ACOES)).map(caixaDe)
      aplicar(elevacaoSobreAsAcoes(caixaDe(elemento), elevacaoAplicada.current, acoes, visivel))
    }
    medir()
    destinoDosAvisos.addEventListener('scroll', medir, true)
    window.addEventListener('resize', medir)
    const observador = new ResizeObserver(medir)
    observador.observe(elemento)
    observador.observe(rodape)
    /*
      O rodapé que não é fixo também MUDA DE LUGAR sem rolagem e sem mudar de tamanho: o conteúdo acima dele encolhe
      (desmarcar um item recolhe o Planejamento, o erro some) ou o painel encolhe com ele (R-E5T-04 da revisão,
      DS-654 (t)). Quem anda é algum filho do painel, ou o próprio painel: os dois são observados, e o filho que entra
      depois (o aviso de erro que aparece) passa a ser observado quando chega.
    */
    const painel = rodape.parentElement
    const observarOsFilhos = (): void => {
      if (painel === null) return
      for (const filho of Array.from(painel.children)) observador.observe(filho)
    }
    if (painel !== null) observador.observe(painel)
    observarOsFilhos()
    const filhos = new MutationObserver(() => { observarOsFilhos(); medir() })
    if (painel !== null) filhos.observe(painel, { childList: true })
    return () => {
      destinoDosAvisos.removeEventListener('scroll', medir, true)
      window.removeEventListener('resize', medir)
      observador.disconnect()
      filhos.disconnect()
    }
  }, [destinoDosAvisos, toasts])

  const camada = (
    <div
      ref={pilha}
      className="pointer-events-none fixed right-4 z-[60] space-y-2"
      /*
        A pilha SOBE quando existe uma barra de ação fixa embaixo.

        Ela era `bottom-4`, e as três telas de formulário têm um rodapé `fixed bottom-0`
        com o Salvar em `justify-end` — as duas caixas se sobrepõem exatamente sobre o
        botão. Subir o `z-index` da barra não resolveria: aí a barra cobriria o toast, e o
        aviso é que passaria a ser inalcançável. Quem cede espaço é a camada que não é ato.

        E não passa sozinho: para chegar ao Salvar o ponteiro atravessa o toast, e passar
        por cima dele CONGELA os seis segundos — ele fica ali enquanto o cursor estiver a
        caminho. O defeito não se resolvia esperando.

        A altura vem MEDIDA da própria barra (`useRodapeFixo`), não de um número escrito
        aqui: rodapé que cresce com um banner de erro deixaria a conta velha.
      */
      style={{ bottom: elevacao === 0 ? 'calc(1rem + var(--dv-rodape-fixo, 0px))' : `calc(1rem + var(--dv-rodape-fixo, 0px) + ${String(elevacao)}px)` }}
      data-teste="toasts"
      /*
        `aria-live` SEM `role="status"`, e a diferença importa.

        As duas coisas fazem a região ser anunciada; `role="status"` faz mais uma, que
        aqui atrapalha: ele dá à pilha o PAPEL de status. A tela 8.5 tem um balão de
        gráfico que é, esse sim, um status — e passou a haver dois papéis "status" na
        página, deixando ambíguo quem procurasse por papel. A pilha de toasts é uma
        região viva, não um status; declarar só o que ela é resolve os dois lados.
      */
      aria-live="polite"
      aria-atomic="false"
      onMouseEnter={pausar}
      onMouseLeave={retomar}
      onFocusCapture={pausar}
      onBlurCapture={retomar}
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          {...(t.tom === 'erro' ? { role: 'alert' } : {})}
          className="pointer-events-auto relative isolate flex min-w-[260px] max-w-[360px] items-start gap-2.5 rounded-[10px] border border-hairline bg-white px-3.5 py-3 text-sm text-text2 shadow-card"
        >
          <Icone nome={VISUAL[t.tom].icone} className={`w-[18px] h-[18px] shrink-0 mt-0.5 ${VISUAL[t.tom].cor}`} />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-navy">{t.titulo}</p>
            {t.descricao !== undefined && <p className="text-[12px] text-muted mt-0.5">{t.descricao}</p>}
            {t.acao !== undefined && (
              <button
                type="button"
                className="touch-44 relative z-10 -ml-2 mt-0.5 inline-flex items-center rounded-lg px-2 text-[12px] font-medium text-navy dv-link"
                /*
                  A recusa do servidor APARECE. Antes, `.finally(fechar)` sem `.catch()`
                  transformava a rejeição em *unhandled*: o toast fechava, e quem clicou
                  ficava com a impressão de que desfez. São 69 sítios em mais de vinte
                  rotas, e todos passavam por aqui — a garantia mora no componente, não em
                  cada chamador, senão a próxima ação nasce sem ela.

                  O toast fecha nos dois caminhos: no sucesso porque o ato acabou, e na
                  falha porque o "Desfazer" que falhou não vale um segundo clique — o que
                  fica na tela é o motivo.
                */
                onClick={() => {
                  void Promise.resolve(t.acao?.aoClicar())
                    .catch((causa: unknown) => {
                      mostrar({
                        tom: 'erro',
                        titulo: 'Não foi possível desfazer',
                        ...(causa instanceof Error && causa.message !== '' ? { descricao: causa.message } : {}),
                      })
                    })
                    .finally(() => {
                      fechar(t.id)
                    })
                }}
              >
                {t.acao.rotulo}
              </button>
            )}
          </div>
          <button
            type="button"
            className="touch-44 -mr-2 -mt-2 grid place-items-center rounded-lg text-faint hover:bg-navy/5 hover:text-text2"
            /* Marca própria: procurar "Fechar" pelo NOME acha um botão por toast vivo,
               e a pilha ainda é portada para dentro de diálogos (ver `sobreposicao.tsx`). */
            data-teste="fechar-toast"
            aria-label={texto('fechar')}
            onClick={() => {
              fechar(t.id)
            }}
          >
            <Icone nome="x" className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  )

  return (
    <Contexto.Provider value={valor}>
      {children}
      {createPortal(camada, destinoDosAvisos ?? document.body)}
    </Contexto.Provider>
  )
}

/**
 * Reserva, na pilha de toasts, o espaço de uma barra de ação FIXA.
 *
 * Devolve um `ref` para pendurar na barra. Enquanto ela estiver montada, a altura medida
 * vira a variável `--dv-rodape-fixo`, e a pilha de toasts se apoia nela em vez de deitar
 * sobre o botão. Ao desmontar, a variável sai e os toasts voltam ao chão.
 *
 * A altura é MEDIDA, e observada: a barra cresce quando um banner de erro entra nela, e um
 * número escrito à mão ficaria velho na primeira vez que isso acontecesse.
 */
export function useRodapeFixo(): RefObject<HTMLDivElement | null> {
  const referencia = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const elemento = referencia.current
    if (elemento === null) return
    const raiz = document.documentElement
    const medir = (): void => {
      raiz.style.setProperty('--dv-rodape-fixo', `${String(Math.ceil(elemento.getBoundingClientRect().height))}px`)
    }
    medir()
    const observador = new ResizeObserver(medir)
    observador.observe(elemento)
    return () => {
      observador.disconnect()
      raiz.style.removeProperty('--dv-rodape-fixo')
    }
  }, [])
  return referencia
}
